import { StrictMode, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { ChakraProvider } from '@chakra-ui/react';
import { constants, theme } from '@sj/sj-theme';
import { Global } from '@emotion/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { App } from './App';
import reportWebVitals from './reportWebVitals';
import { AppProvider } from './context';
import { getSentryConfig } from './environment';
import { CustomBrowserRouter } from './CustomBrowserRouter';

// if (import.meta.env.MODE === 'development') {
//   // eslint-disable-next-line
//   const { worker } = require('./mocks/browser');
//   if (process.env.REACT_APP_MOCK_URLS === 'true') {
//     worker.start();
//   }
// }

const sentryConfig = getSentryConfig(window.location);

Sentry.init({
  dsn: 'https://d90bb111b0a84c65a1051b60d191f480@o1429225.ingest.sentry.io/4505211100397568',
  transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),
  enabled: sentryConfig.enabled,
  release: sentryConfig.release,
  environment: sentryConfig.environment,
  sendDefaultPii: true,
  attachStacktrace: true,
  // This error is from Instagram browser and made sure no user impacted so far
  ignoreErrors: [
    /Can't find variable: _AutofillCallbackHandler/i,
    /Can't find variable: jQuery/i,
    /jQuery is not defined/i,
  ],
  // These domains are external for our app
  denyUrls: [
    /https:\/\/assets\.adobedtm\.com/,
    /https:\/\/cdn\.cookielaw\.org/,
    /https:\/\/maps\.googleapis\.com/,
    /https:\/\/s.go-mpulse\.net/,
  ],
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.extraErrorDataIntegration(),
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration({
      maskAllText: false,
      maskAllInputs: true,
      blockAllMedia: false,
      networkDetailAllowUrls: [
        'https://fundraising.qa.stjude.org/',
        'https://fundraising.stjude.org/',
      ],
    }),
  ],
  sampleRate: sentryConfig.sampleRate,
  tracesSampleRate: sentryConfig.tracesSampleRate,
  replaysSessionSampleRate: sentryConfig.replaysSessionSampleRate,
  replaysOnErrorSampleRate: sentryConfig.replaysOnErrorSampleRate,
});

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

root.render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
      <QueryClientProvider client={queryClient}>
        {/* <BrowserRouter basename="site/Donation2"> */}
        <CustomBrowserRouter>
          <ChakraProvider theme={theme}>
            <Global styles={constants.SJ_FONT_FACE} />
            <AppProvider>
              <App />
            </AppProvider>
          </ChakraProvider>
        </CustomBrowserRouter>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

/** These are TR Global styles, once in QA/PROD our own styles gets overridden because of this file getting added
 * So removing it for this project. All the global styles should be added as part of Chakra theme */
document.querySelector('link[href*="../css/UserGlobalStyle.css"]')?.remove();
document.querySelector('link[href*="../css/CustomStyle.css"]')?.remove();
